import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '#environments';

export interface PaProcedureTemplate {
  id: number;
  templateName: string;
  fot: string | null;
  category: string | null;
  isPublic: boolean;
  procedureDependencies: string | null;
  ownerUserId: string;
  isOwner: boolean;
  createdBy: string | null;
}

export interface PaProcedureTemplateUpsertRequest {
  templateName: string;
  fot: string | null;
  category: string | null;
  isPublic: boolean;
  procedureDependencies: string | null;
}

export interface SlaConfiguration {
  slaKey: string;
  goalValue: number | null;
  deadlineValue: number | null;
  passedDeadlineValue: number | null;
  isBusinessDay: boolean;
  maxRepeatEvents: number | null;
}

export interface SlaConfigurationUpdateRequest {
  goalValue?: number | null;
  deadlineValue?: number | null;
  passedDeadlineValue?: number | null;
  isBusinessDay?: boolean;
  maxRepeatEvents?: number | null;
}

export interface PlantCodeAdminDto {
  id: number;
  plantCode: string;
  companyCode: string;
  description: string | null;
}

export interface PlantCodeBatchItemRequest {
  id: number | null;
  plantCode: string;
  companyCode: string;
  description: string | null;
  isDelete: boolean;
}

export interface PlantCodeBatchRequest {
  items: PlantCodeBatchItemRequest[];
}

export interface GaPercentDto {
  id: number;
  displayValue: number;
  storedValue: number;
}

export interface UpdateGaPercentRequest {
  displayValue: number;
}

export interface PowerlineDistanceDto {
  id: number;
  feet: number;
}

export interface UpdatePowerlineDistanceRequest {
  feet: number;
}

export interface DropdownOptionDto {
  id: number;
  lookupGroup: string;
  value: string;
  displayText: string | null;
  dependencyGroup: string | null;
  dependencyValue: string | null;
  sortOrder: number;
  isPendingDelete: boolean;
  isNew: boolean;
}

export interface DropdownOptionSubmitItemRequest {
  id: number | null;
  value: string;
  displayText: string | null;
  dependencyGroup: string | null;
  dependencyValue: string | null;
  sortOrder: number;
  isDelete: boolean;
}

export interface DropdownOptionsSubmitRequest {
  lookupGroup: string;
  items: DropdownOptionSubmitItemRequest[];
}

export type HistoricalJobsViewKey =
  | 'pull-requests'
  | 'rigless'
  | 'plug-abandonment'
  | 'reclamations'
  | 'facility-decommission';

export interface HistoricalJob {
  caseNumber: string;
  wellRef: string | null;
  api10: string | null;
  operationalDate: string | null;
  status: string | null;
}

export interface ScheduleCrewDto {
  id: number;
  crewName: string;
  crewType: string;
}

export interface AddScheduleCrewRequest {
  crewName: string;
}

export interface WinCoordinatorDto {
  id: number;
  state: string;
  emailValue: string | null;
  dependencyValue: string | null;
}

export interface WinCoordinatorUpdateItem {
  id: number | null;
  state: string;
  emailValue: string;
  isDelete: boolean;
}

export interface WinCoordinatorsUpdateRequest {
  items: WinCoordinatorUpdateItem[];
}

export interface RecipientDto {
  id: number;
  email: string;
  location: string | null;
  isPendingDelete: boolean;
}

export interface RecipientSubmitItem {
  id: number | null;
  email: string;
  location?: string;
  isDelete: boolean;
}

export interface ReclamationSubmitRequest {
  items: RecipientSubmitItem[];
}

export interface RodTubingSubmitRequest {
  items: RecipientSubmitItem[];
}

export interface TitleClearanceRecipientsSubmitRequest {
  items: RecipientSubmitItem[];
}

@Injectable({ providedIn: 'root' })
export class OptionsApiService {
  private _http = inject(HttpClient);
  private readonly _baseUrl = environment.apiUrl;

  // ─── Distribution Recipients ─────────────────────────────────────────────

  getWinStates(): Observable<string[]> {
    return this._http.get<string[]>(`${this._baseUrl}/distributions/win-coordinators/states`);
  }

  getWinCoordinators(): Observable<WinCoordinatorDto[]> {
    return this._http.get<WinCoordinatorDto[]>(`${this._baseUrl}/distributions/win-coordinators`);
  }

  updateWinCoordinators(request: WinCoordinatorsUpdateRequest): Observable<void> {
    return this._http.post<void>(`${this._baseUrl}/distributions/win-coordinators`, request);
  }

  getReclamationRecipients(): Observable<RecipientDto[]> {
    return this._http.get<RecipientDto[]>(`${this._baseUrl}/distributions/reclamation`);
  }

  submitReclamationRecipients(request: ReclamationSubmitRequest): Observable<void> {
    return this._http.post<void>(`${this._baseUrl}/distributions/reclamation`, request);
  }

  getRodTubingRecipients(): Observable<RecipientDto[]> {
    return this._http.get<RecipientDto[]>(`${this._baseUrl}/distributions/rod-tubing-retrieval`);
  }

  submitRodTubingRecipients(request: RodTubingSubmitRequest): Observable<void> {
    return this._http.post<void>(`${this._baseUrl}/distributions/rod-tubing-retrieval`, request);
  }

  getTitleClearanceRecipients(): Observable<RecipientDto[]> {
    return this._http.get<RecipientDto[]>(`${this._baseUrl}/distributions/title-clearance-recipients`);
  }

  submitTitleClearanceRecipients(request: TitleClearanceRecipientsSubmitRequest): Observable<void> {
    return this._http.post<void>(`${this._baseUrl}/distributions/title-clearance-recipients`, request);
  }

  // ─── Schedule Crews ─────────────────────────────────────────────────────

  getPlantCodes(): Observable<PlantCodeAdminDto[]> {
    return this._http.get<PlantCodeAdminDto[]>(`${this._baseUrl}/plant-codes`);
  }

  submitPlantCodesBatch(request: PlantCodeBatchRequest): Observable<void> {
    return this._http.post<void>(`${this._baseUrl}/plant-codes/batch`, request);
  }

  // ─── Data Values — Asset Retirement ─────────────────────────────────────

  getGaPercent(): Observable<GaPercentDto> {
    return this._http.get<GaPercentDto>(`${this._baseUrl}/data-values/asset-retirement/ga-percent`);
  }

  updateGaPercent(request: UpdateGaPercentRequest): Observable<GaPercentDto> {
    return this._http.put<GaPercentDto>(`${this._baseUrl}/data-values/asset-retirement/ga-percent`, request);
  }

  getPowerlineDistance(): Observable<PowerlineDistanceDto> {
    return this._http.get<PowerlineDistanceDto>(`${this._baseUrl}/data-values/asset-retirement/powerline-distance`);
  }

  updatePowerlineDistance(request: UpdatePowerlineDistanceRequest): Observable<PowerlineDistanceDto> {
    return this._http.put<PowerlineDistanceDto>(`${this._baseUrl}/data-values/asset-retirement/powerline-distance`, request);
  }

  getDropdownOptions(lookupGroup: string): Observable<DropdownOptionDto[]> {
    const params = new HttpParams().set('group', lookupGroup);
    return this._http.get<DropdownOptionDto[]>(`${this._baseUrl}/data-values/dropdown-options`, { params });
  }

  getDropdownOptionLookupGroups(): Observable<string[]> {
    return this._http.get<string[]>(`${this._baseUrl}/data-values/dropdown-option-groups`);
  }

  submitDropdownOptions(request: DropdownOptionsSubmitRequest): Observable<void> {
    return this._http.post<void>(`${this._baseUrl}/data-values/dropdown-options`, request);
  }

  // ─── Schedule Crews ─────────────────────────────────────────────────────

  getScheduleCrews(): Observable<ScheduleCrewDto[]> {
    return this._http.get<ScheduleCrewDto[]>(`${this._baseUrl}/schedule-crews`);
  }

  addScheduleCrew(request: AddScheduleCrewRequest): Observable<ScheduleCrewDto> {
    return this._http.post<ScheduleCrewDto>(`${this._baseUrl}/schedule-crews`, request);
  }

  deleteScheduleCrew(id: number): Observable<void> {
    return this._http.delete<void>(`${this._baseUrl}/schedule-crews/${id}`);
  }

  // ─── PA Procedure Templates ───────────────────────────────────────────────

  getPaProcedureTemplates(): Observable<PaProcedureTemplate[]> {
    return this._http.get<PaProcedureTemplate[]>(`${this._baseUrl}/pa-procedure-templates`);
  }

  createPaProcedureTemplate(request: PaProcedureTemplateUpsertRequest): Observable<PaProcedureTemplate> {
    return this._http.post<PaProcedureTemplate>(`${this._baseUrl}/pa-procedure-templates`, request);
  }

  updatePaProcedureTemplate(id: number, request: PaProcedureTemplateUpsertRequest): Observable<PaProcedureTemplate> {
    return this._http.put<PaProcedureTemplate>(`${this._baseUrl}/pa-procedure-templates/${id}`, request);
  }

  copyPaProcedureTemplate(id: number): Observable<PaProcedureTemplate> {
    return this._http.post<PaProcedureTemplate>(`${this._baseUrl}/pa-procedure-templates/${id}/copy`, {});
  }

  deletePaProcedureTemplate(id: number): Observable<void> {
    return this._http.delete<void>(`${this._baseUrl}/pa-procedure-templates/${id}`, {
      body: { confirmed: true },
    });
  }

  // ─── SLA Configuration ─────────────────────────────────────────────────

  getSlaConfigurations(): Observable<SlaConfiguration[]> {
    return this._http.get<SlaConfiguration[]>(`${this._baseUrl}/sla-configuration`);
  }

  getSlaConfigurationByKey(slaKey: string): Observable<SlaConfiguration> {
    return this._http.get<SlaConfiguration>(`${this._baseUrl}/sla-configuration/${slaKey}`);
  }

  updateSlaConfiguration(slaKey: string, request: SlaConfigurationUpdateRequest): Observable<SlaConfiguration> {
    return this._http.put<SlaConfiguration>(`${this._baseUrl}/sla-configuration/${slaKey}`, request);
  }

  // ─── Historical Jobs ───────────────────────────────────────────────────

  getHistoricalJobs(view: HistoricalJobsViewKey, dateFrom: string, dateTo: string): Observable<HistoricalJob[]> {
    const params = new HttpParams()
      .set('dateFrom', dateFrom)
      .set('dateTo', dateTo);

    return this._http.get<HistoricalJob[]>(`${this._baseUrl}/historical-jobs/${view}`, { params });
  }

  exportHistoricalJobs(view: HistoricalJobsViewKey, dateFrom: string, dateTo: string): Observable<Blob> {
    const params = new HttpParams()
      .set('dateFrom', dateFrom)
      .set('dateTo', dateTo);

    return this._http.get(`${this._baseUrl}/historical-jobs/${view}/export`, {
      params,
      responseType: 'blob',
    });
  }
}
